import {
  SearchApi,
  SearchDocSiteRequest,
  DocSiteResult as DocSiteResultRaw,
  DocSiteSearchResponse as DocSiteSearchResponseRaw,
} from '@rbx/clients/creatorResourcesSearchApi';
import { Configuration } from '@rbx/clients';
import { validate } from '@modules/utilities/utils/enum';
import { getBEDEV2ServiceBasePath } from '@modules/utilities/constants/baseUrl';
import { DocumentationContentType as DocsSearchDocumentationContentType } from '@rbx/creator-docs-search';
import throwError from '@modules/utilities/error';
import { TTranslateFunction } from '@modules/localization/types/TranslateFunction';
import {
  DocumentationContentType,
  DocumentationSubType,
  DocumentationThirdType,
  DocumentationCloudApiSubType,
  DocumentationLuaSubType,
  DocumentationArticleSubType,
  DocumentationLuaType,
  DocumentationCloudApiType,
  DocumentationDevForumSubType,
} from './docSiteSearchType';
import CategoryTitles from './CategoryTitles';

const configuration = new Configuration({
  basePath: getBEDEV2ServiceBasePath('creator-resources-search-api'),
  credentials: 'include',
});
const searchApi = new SearchApi(configuration);

export type DocSiteResult = Omit<
  DocSiteResultRaw,
  'documentationContentType' | 'documentationSubType' | 'documentationThirdType'
> & {
  documentationContentType: DocumentationContentType | null;
  documentationSubType: DocumentationSubType | null;
  documentationThirdType: DocumentationThirdType | null;
  url: string | null;
  highlightedTitle?: string | null;
  highlightedDisplayedSummary?: string | null;
};

export type DocSiteSearchResponse = Omit<DocSiteSearchResponseRaw, 'results'> & {
  isError: boolean;
  results: DocSiteResult[];
};

export interface SearchClient {
  search(request: SearchDocSiteRequest): Promise<DocSiteSearchResponse>;
}

export function getSearchResultUrl(result: DocSiteResultRaw): string | null {
  const { resultTargetReference: reference } = result;
  return reference ?? null;
}

export function parseDocumentationContentType(str: string): DocumentationContentType | null {
  if (validate(DocumentationContentType, str)) {
    return str as DocumentationContentType;
  }
  return null;
}

export function parseDocumentationSubType(str: string): DocumentationSubType | null {
  if (validate(DocumentationArticleSubType, str)) {
    return str as DocumentationArticleSubType;
  }
  if (validate(DocumentationLuaType, str)) {
    return str as DocumentationLuaType;
  }
  if (validate(DocumentationCloudApiType, str)) {
    return str as DocumentationCloudApiType;
  }
  if (validate(DocumentationDevForumSubType, str)) {
    return str as DocumentationDevForumSubType;
  }
  return null;
}

export function parseDocumentationThirdType(str: string): DocumentationThirdType | null {
  if (validate(DocumentationCloudApiSubType, str)) {
    return str as DocumentationCloudApiSubType;
  }
  if (validate(DocumentationLuaSubType, str)) {
    return str as DocumentationLuaSubType;
  }
  return null;
}

const resolveSearchResults = (response: DocSiteSearchResponseRaw) => {
  if (response.results) {
    return {
      ...response,
      isError: false,
      results: response.results.map((result) => ({
        ...result,
        documentationContentType: parseDocumentationContentType(
          result.documentationContentType ?? '',
        ),
        documentationSubType: parseDocumentationSubType(result.documentationSubType ?? ''),
        documentationThirdType: parseDocumentationThirdType(result.documentationThirdType ?? ''),
        url: getSearchResultUrl(result),
      })),
    };
  }
  return { ...response, isError: false, results: [] };
};

export const searchClient: SearchClient = {
  async search({
    keyword,
    documentationContentType = '',
    documentationSubType = '',
    documentationThirdType = '',
    tag = '',
    isFuzzyMatch = false,
    pageSize = 10,
    pageIndex,
    locale = '',
    searchSessionId,
  }: SearchDocSiteRequest): Promise<DocSiteSearchResponse> {
    const makeRequest = async (
      requestParams: SearchDocSiteRequest,
    ): Promise<DocSiteSearchResponse> => {
      const response = await searchApi.searchSearchDocSite({
        searchSearchDocSiteRequest: requestParams,
      });
      return resolveSearchResults(response);
    };

    const delay = (ms: number): Promise<void> =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      });

    const executeWithRetry = async (
      params: SearchDocSiteRequest,
      retryCount: number,
      hasTriedWithoutLocale: boolean,
    ): Promise<DocSiteSearchResponse> => {
      const maxRetries = 3;

      try {
        const results = await makeRequest(params);

        // If results are empty and we have a locale and haven't tried without locale yet
        if (
          (!results.results || results.results.length === 0) &&
          locale &&
          !hasTriedWithoutLocale
        ) {
          // Retry without locale
          const paramsWithoutLocale = {
            ...params,
            locale: '',
          };
          const resultsWithoutLocale = await executeWithRetry(
            paramsWithoutLocale,
            retryCount,
            true,
          );
          if (
            !resultsWithoutLocale ||
            !resultsWithoutLocale.results ||
            resultsWithoutLocale.results.length === 0
          ) {
            return {
              isError: false,
              results: [],
            };
          }

          const resultsWithLocaleResults = resultsWithoutLocale.results.map((result) => {
            const {
              elasticsearchDocumentId,
              url,
              documentationContentType: resultDocumentationContentType,
            } = result;

            if (!elasticsearchDocumentId || !url) {
              return result;
            }

            // Elasticsearch index document id format: https://github.com/Roblox/creator-docs-internal-search/blob/main/src/compile/utils/indexDocumentationUtils.ts#L16
            const elasticsearchDocumentIdSegments = elasticsearchDocumentId.split(':');
            let extractLocale =
              elasticsearchDocumentIdSegments[
                elasticsearchDocumentIdSegments.length - 1
              ].toLowerCase();
            // Following case doesn't need to add locale prefix:
            // 1. locale is `en-us`
            // 2. url already has locale prefix
            // 3. locale is not valid
            // 4. content type is `Video`, `DevForum`
            if (
              extractLocale === 'en-us' || // locale is `en-us`
              !/^[a-z]{2}-[a-z]{2}$/.test(extractLocale) || // locale is not valid
              (resultDocumentationContentType &&
                [
                  DocsSearchDocumentationContentType.Video,
                  DocsSearchDocumentationContentType.DevForum,
                ].includes(resultDocumentationContentType)) || // content type is `Video`, `DevForum`
              /^\/[a-z]{2}-[a-z]{2}\/.*$/.test(url) // url already has locale prefix
            ) {
              extractLocale = '';
            }

            return {
              ...result,
              url: extractLocale ? `/${extractLocale}${url}` : url,
            };
          });

          return {
            ...resultsWithoutLocale,
            results: resultsWithLocaleResults,
          };
        }

        return results;
      } catch (error) {
        // Check if it's a 5xx error
        const isServerError =
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof (error as { response: { status: number } }).response?.status === 'number' &&
          (error as { response: { status: number } }).response.status >= 500 &&
          (error as { response: { status: number } }).response.status < 600;

        if (isServerError && retryCount < maxRetries) {
          await delay(200 * (retryCount + 1));
          return executeWithRetry(params, retryCount + 1, hasTriedWithoutLocale);
        }

        throwError(`searchClient: failed to fetch search results ${error}`);
        return {
          isError: true,
          results: [],
        };
      }
    };

    const searchDocSiteRequest = {
      keyword,
      isFuzzyMatch,
      documentationContentType,
      documentationSubType,
      documentationThirdType,
      tag,
      pageIndex,
      pageSize,
      locale,
      searchSessionId,
    } as SearchDocSiteRequest;

    return executeWithRetry(searchDocSiteRequest, 0, false);
  },
};

export default searchClient;

export function getCategoryName(result: DocSiteResult): string | null {
  const {
    documentationContentType: contentType,
    documentationSubType: subType,
    documentationThirdType: thirdType,
  } = result;
  if (contentType === null) {
    return null;
  }
  if (contentType === DocumentationContentType.LuaAPI) {
    const engineTitle = CategoryTitles.Engine;
    if (subType === null) {
      return engineTitle;
    }
    if (thirdType === null) {
      return `${engineTitle}: ${subType}`;
    }
    return `${engineTitle}: ${subType}: ${thirdType}`;
  }
  if (contentType === DocumentationContentType.Article) {
    return subType;
  }
  if (contentType === DocumentationContentType.CloudAPI) {
    return CategoryTitles.Cloud;
  }
  if (contentType === DocumentationContentType.Video) {
    return CategoryTitles.Video;
  }
  if (contentType === DocumentationContentType.DevForum) {
    const devForumTitle = CategoryTitles.DevForum;
    if (subType === null) {
      return devForumTitle;
    }
    if (thirdType === null) {
      return `${devForumTitle}: ${subType}`;
    }
    return `${devForumTitle}: ${subType}: ${thirdType}`;
  }
  return null;
}

export function getCategoryTranslationLabels(result: DocSiteResult): string[] {
  const {
    documentationContentType: contentType,
    documentationSubType: subType,
    documentationThirdType: thirdType,
  } = result;
  if (contentType === null) {
    return [];
  }
  if (contentType === DocumentationContentType.CloudAPI) {
    const labels = ['Label.Cloud'];
    switch (subType) {
      case DocumentationCloudApiType.Legacy:
        labels.push('Label.CloudAPIVersionLegacy');
        break;
      case DocumentationCloudApiType.V1:
        labels.push('Label.CloudAPIVersionV1');
        break;
      case DocumentationCloudApiType.V2:
        labels.push('Label.CloudAPIVersionV2');
        break;
      case DocumentationCloudApiType.Features:
        labels.push('Label.CloudAPIVersionFeatures');
        break;
      default:
        return labels;
    }
    switch (thirdType) {
      case DocumentationCloudApiSubType.Endpoint:
        labels.push('Label.CloudAPITypeEndpoint');
        break;
      case DocumentationCloudApiSubType.API:
        labels.push('Label.CloudAPITypeAPI');
        break;
      case DocumentationCloudApiSubType.Resource:
        labels.push('Label.CloudAPITypeResource');
        break;
      default:
        return labels;
    }
    return labels;
  }
  return [];
}

export const getTranslatedCategoryDisplayText = (
  result: DocSiteResult,
  translate: TTranslateFunction,
): string | null => {
  const {
    documentationContentType: contentType,
    documentationSubType: subType,
    documentationThirdType: thirdType,
  } = result;
  if (contentType === null) {
    return null;
  }
  if (contentType === DocumentationContentType.LuaAPI) {
    if (subType === null) {
      return translate(`Label.Engine`);
    }
    if (thirdType === null) {
      return translate(`Label.${subType}`);
    }
    return `${translate(`Label.${subType}`)} ${translate(`Label.${thirdType}`)}`;
  }
  if (contentType === DocumentationContentType.Article) {
    return translate(`Label.${subType}`);
  }
  if (contentType === DocumentationContentType.CloudAPI) {
    return getCategoryTranslationLabels(result)
      .map((label) => translate(label))
      .join(' ');
  }
  if (contentType === DocumentationContentType.Video) {
    return translate('Label.Video');
  }
  if (contentType === DocumentationContentType.DevForum) {
    const devForumLabel = translate(`Label.DevForum`);
    if (subType === null) {
      return devForumLabel;
    }
    if (thirdType === null) {
      return `${devForumLabel} ${translate(`Label.${subType}`)}`;
    }
    return `${devForumLabel} ${translate(`Label.${subType}`)} ${translate(`Label.${thirdType}`)}`;
  }
  return `${contentType} ${subType ?? ''} ${thirdType ?? ''}`;
};
